import { marketBaseOf } from "@local/i18n/src/languages"
import type { Currency, MarketData } from "./const/marketsData"
import { defaultMarketData, marketsData } from "./const/marketsData"

/**
 * @description - list of countries inside the EU zone
 */
export const EU_COUNTRIES = new Set<string>([
  "AT",
  "BE",
  "BG",
  "HR",
  "CY",
  "CZ",
  "DK",
  "EE",
  "FI",
  "FR",
  "DE",
  "GR",
  "HU",
  "IE",
  "IT",
  "LV",
  "LT",
  "LU",
  "MT",
  "NL",
  "PL",
  "PT",
  "RO",
  "SK",
  "SI",
  "ES",
  "SE",
])

/**
 * Abstract internationalization (i18n) class providing static methods for market, currency, and country operations.
 *
 * @remarks
 * This class consolidates all i18n functionality into a single interface with static methods.
 * It provides a centralized way to handle market data lookups, currency conversions, and country mappings.
 *
 * @example
 * ```typescript
 * // Get market data
 * const marketData = I18n.getMarketData("us")
 *
 * // Get currency information
 * const currencyCode = I18n.getCurrencyCodeFromMarket("gb") // "GBP"
 * const currencySymbol = I18n.getCurrencySymbolFromMarket("gb") // "£"
 *
 * // Convert between countries and markets
 * const market = I18n.getMarketFromCountry("FR") // "eu"
 * const marketFromCurrency = I18n.getMarketFromCurrency("EUR") // "eu"
 *
 * // Check EU membership
 * const isEU = I18n.isCountryInsideEU("DE") // true
 *
 * // Build market URLs
 * const newUrl = I18n.buildMarketUrl("https://example.com/us/products", "us", "gb")
 * ```
 */
export abstract class I18n {
  /**
   * Gets the complete market data for a given market identifier.
   * This is the core method that other helpers can use as a jumpstart.
   *
   * @param market - Market slug such as "us" or "gb"; case-insensitive and trimmed before lookup.
   * @returns The complete market data object, or the default market data when no match is found.
   *
   * @example
   * I18n.getMarketData("us") // { market: "us", currencyCode: "USD", currencySymbol: "$", locale: "en", name: "United States" }
   * I18n.getMarketData("GB") // { market: "gb", currencyCode: "GBP", currencySymbol: "£", locale: "en", name: "United Kingdom" }
   * I18n.getMarketData("unknown") // Returns default market data (US)
   */
  static getMarketData(market: string | null | undefined): MarketData {
    // DEV-9850: variant slugs ("de-en") carry the base market's data.
    const normalizedMarket = marketBaseOf(market)

    if (!normalizedMarket) {
      return defaultMarketData
    }

    const matchedMarket = marketsData.find(
      (marketData) => marketData.market === normalizedMarket
    )

    return matchedMarket || defaultMarketData
  }

  /**
   * Derives the ISO 4217 currency code for a given market identifier.
   *
   * @param market - Market slug such as "us" or "gb"; case-insensitive and trimmed before lookup.
   * @returns The currency code configured for the market, or the default market currency when no match is found.
   *
   * @example
   * I18n.getCurrencyCodeFromMarket("us") // "USD"
   * I18n.getCurrencyCodeFromMarket("GB") // "GBP"
   * I18n.getCurrencyCodeFromMarket("unknown") // "USD"
   */
  static getCurrencyCodeFromMarket(
    market: string | null | undefined
  ): Currency {
    return this.getMarketData(market).currencyCode
  }

  /**
   * Retrieves a currency symbol for a given market.
   *
   * @param market - Market slug such as "us" or "gb"; case-insensitive and trimmed before lookup.
   * @returns The currency symbol configured for the market, or the default market symbol when no match is found.
   *
   * @example
   * I18n.getCurrencySymbolFromMarket("us") // "$"
   * I18n.getCurrencySymbolFromMarket("GB") // "£"
   * I18n.getCurrencySymbolFromMarket("") // "$"
   */
  static getCurrencySymbolFromMarket(
    market: string | null | undefined
  ): string {
    return this.getMarketData(market).currencySymbol
  }

  /**
   * Maps a country code to the appropriate market.
   *
   * @param countryCode - The ISO Alpha-2 country code (e.g., "US", "CA", "FR")
   * @returns The market code ("us", "ca", "au", "gb", "ch", "nz", "eu")
   *
   * @example
   * I18n.getMarketFromCountry("US") // "us"
   * I18n.getMarketFromCountry("FR") // "eu"
   * I18n.getMarketFromCountry("CA") // "ca"
   */
  static getMarketFromCountry(countryCode: string): string {
    if (!countryCode) {
      return "us" // Default fallback
    }

    const normalizedCountry = countryCode.toLowerCase()

    // German geo flip (DEV-9539, D6): DE visitors leave the `eu` market for the
    // dedicated German market. Must run BEFORE the EU check, since DE is also an
    // EU country and would otherwise fall through to `eu`.
    if (normalizedCountry === "de") {
      return "de"
    }

    // Check if the country is in the EU
    if (this.isCountryInsideEU(normalizedCountry)) {
      return "eu"
    }

    // Map specific countries to their markets
    switch (normalizedCountry) {
      case "ca":
        return "ca"
      case "au":
        return "au"
      case "gb":
        return "gb"
      case "ch":
        return "ch"
      case "nz":
        return "nz"
      default:
        return "us" // Default fallback for all other countries
    }
  }

  /**
   * Get the market from the currency code.
   *
   * @param currency - The currency code to get the market from
   * @returns The market identifier, or undefined if no match is found
   *
   * @example
   * I18n.getMarketFromCurrency("EUR") // "eu"
   * I18n.getMarketFromCurrency("USD") // "us"
   * I18n.getMarketFromCurrency("UNKNOWN") // undefined
   */
  static getMarketFromCurrency(currency: string): string | undefined {
    const market = marketsData.find(
      (marketData) => marketData.currencyCode === currency.toUpperCase()
    )
    return market?.market
  }

  /**
   * Checks if a country is inside the EU zone.
   *
   * @param country - Country ISO Alpha‑2 code string i.e. "FR", "US", etc
   * @returns true if the country is inside EU zone
   *
   * @example
   * I18n.isCountryInsideEU("FR") // true
   * I18n.isCountryInsideEU("US") // false
   * I18n.isCountryInsideEU("de") // true (case-insensitive)
   */
  static isCountryInsideEU(country: string): boolean {
    return EU_COUNTRIES.has(country.toUpperCase())
  }

  /**
   * Builds a new URL with the origin and destination markets.
   *
   * @param url - The original URL
   * @param origin - The origin market. i.e. "eu"
   * @param destination - The destination market. i.e. "us"
   * @returns The new URL object
   *
   * @example
   * const newUrl = I18n.buildMarketUrl("https://example.com/us/products", "us", "gb")
   * // Returns URL object with pathname "/gb/products"
   */
  static buildMarketUrl(url: string, origin: string, destination: string): URL {
    const parsed = new URL(url) // expects an absolute URL
    const parts = parsed.pathname.split("/").filter(Boolean)
    if (parts.length > 0 && parts[0].toLowerCase() === origin.toLowerCase()) {
      parts[0] = destination.toLowerCase()
      parsed.pathname = `/${parts.join("/")}`
    }
    return parsed
  }

  /**
   * Gets all available markets data.
   *
   * @returns Array of all market data objects
   *
   * @example
   * const allMarkets = I18n.getAllMarkets()
   * // Returns array of all MarketData objects
   */
  static getAllMarkets(): MarketData[] {
    return [...marketsData]
  }

  /**
   * Gets the default market data.
   *
   * @returns The default market data object (US market)
   *
   * @example
   * const defaultMarket = I18n.getDefaultMarket()
   * // Returns US market data
   */
  static getDefaultMarket(): MarketData {
    return defaultMarketData
  }

  /**
   * Checks if a market identifier exists in the available markets.
   *
   * @param market - Market slug to check
   * @returns true if the market exists, false otherwise
   *
   * @example
   * I18n.isValidMarket("us") // true
   * I18n.isValidMarket("invalid") // false
   */
  static isValidMarket(market: string | null | undefined): boolean {
    if (!market) return false
    const normalizedMarket = market.trim().toLowerCase()
    return marketsData.some(
      (marketData) => marketData.market === normalizedMarket
    )
  }

  /**
   * Maps a currency code to valid ISO 3166-1 alpha-2 country code(s).
   *
   * @param currency - ISO 4217 currency code (e.g., "USD", "EUR", "GBP")
   * @returns A single country code string or an array of country codes for multi-country currencies like EUR.
   *
   * @example
   * I18n.getCountryCodesFromCurrency("USD") // "US"
   * I18n.getCountryCodesFromCurrency("GBP") // "GB"
   * I18n.getCountryCodesFromCurrency("EUR") // ["AT", "BE", "BG", ...]
   */
  static getCountryCodesFromCurrency(currency: string): string | string[] {
    if (!currency) return "US"
    const map: Record<string, string | string[]> = {
      USD: "US",
      GBP: "GB",
      AUD: "AU",
      NZD: "NZ",
      CAD: "CA",
      CHF: "CH",
      EUR: Array.from(EU_COUNTRIES),
    }
    return map[currency.toUpperCase()] ?? "US"
  }
}

// Export types
export type { Currency, MarketData } from "./const/marketsData"
